import { ApiRequestError } from "./api";

const chineseText = /[\u3400-\u9fff]/u;
const internalCodeToken = /\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/u;
const permissionDeniedCodes = new Set([
  "AUTHORIZATION_DEFAULT_DENY",
  "AUTHORIZATION_DENIED",
  "AUTHORIZATION_IDENTITY_REJECTED",
  "AUTHORIZATION_ROLE_REJECTED",
  "AUTHORIZATION_SCOPE_MISMATCH",
  "FORBIDDEN",
]);

const transactionErrorMessages: Readonly<Record<string, string>> = {
  ORDER_NOT_FOUND: "当前门店找不到这笔订单。请返回订单列表重新查找，确认门店和营业日是否正确",
  TABLE_NOT_FOUND: "当前门店找不到这张球台。请刷新球台列表后重新选择",
  TABLE_VERSION_CONFLICT: "球台状态已经被其他操作更新。请刷新页面核对最新球台状态后再操作",
  TABLE_SESSION_CHANGED:
    "这张球台已经进入另一段开台记录。请刷新页面，不要把原操作用到新顾客的订单上",
  ORDER_AGGREGATE_VERSION_CONFLICT:
    "订单内容已经被其他操作更新。请重新打开原订单，核对金额和项目后再操作",
  CHECKOUT_TOTAL_CHANGED: "结账金额已经发生变化。请重新核对订单明细和应收金额后再确认收款",
  PAYMENT_METHOD_DISABLED: "当前门店已经停用这种收款方式。请选择页面仍允许的收款方式",
  PAYMENT_OR_SYNC_CONFLICT:
    "这笔订单的收款或同步记录需要人工复核。请保留原订单，在待处理记录中核对，不要重复收款",
  IDEMPOTENCY_COLLISION:
    "原操作编号已经对应另一份内容，系统没有再次执行。请刷新页面后重新发起正确操作",
  IDEMPOTENCY_KEY_REUSED:
    "原操作编号已经使用，系统没有再次执行。请刷新页面核对结果后重新发起正确操作",
};

function safeChineseMessage(message: string, code?: string): string | null {
  const normalized = message.trim();
  if (
    normalized.length === 0 ||
    !chineseText.test(normalized) ||
    internalCodeToken.test(normalized) ||
    (code !== undefined && normalized.includes(code))
  ) {
    return null;
  }
  return normalized;
}

function joinReasonAndNextStep(reason: string, nextStep: string): string {
  const normalizedReason = reason.replace(/[。；，、\s]+$/u, "");
  return `${normalizedReason}。${nextStep.replace(/[。；，、\s]+$/u, "")}`;
}

function composableMessage(message: string): string {
  return message.replace(/[。；，、\s]+$/u, "");
}

function isConnectivityFailure(code: string, statusCode?: number): boolean {
  return (
    statusCode === 408 ||
    code === "ONLINE_SERVICE_REQUIRED" ||
    code === "NETWORK_ERROR" ||
    code === "REQUEST_TIMEOUT" ||
    /(?:CLOUD|GATEWAY|COORDINATOR).*(?:UNAVAILABLE|UNREACHABLE|TIMEOUT)/u.test(code)
  );
}

function isConflict(code: string): boolean {
  return /(?:CONFLICT|DUPLICATE|IDEMPOTENCY|KEY_REUSED|ACTOR_MISMATCH)/u.test(code);
}

/**
 * Converts technical request failures into staff-facing Chinese guidance.
 * Internal error codes remain available on the error object for diagnostics,
 * but are never rendered as part of the ordinary business message.
 */
export function businessErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) {
    const { code, statusCode } = error;
    if (code === "INVALID_CREDENTIALS") {
      return safeChineseMessage(error.message, code) ?? "当前账号密码不正确。请重新输入后再试";
    }
    if (statusCode === 401 || code === "AUTH_REQUIRED" || code.includes("SESSION_EXPIRED")) {
      return "登录状态已失效。请重新登录后再试";
    }
    if (statusCode === 403 || permissionDeniedCodes.has(code)) {
      return "当前账号没有完成此操作的权限。请联系店长确认岗位权限";
    }
    if (isConnectivityFailure(code, statusCode)) {
      return "暂时无法连接门店服务。请保留当前页面，恢复联网后按页面提示重试";
    }

    const transactionMessage = transactionErrorMessages[code];
    if (transactionMessage) return transactionMessage;

    const reason = safeChineseMessage(error.message, code);
    if (isConflict(code)) {
      const nextStep = "请刷新页面核对最新内容后再操作";
      return reason ? joinReasonAndNextStep(reason, nextStep) : `页面内容已发生变化。${nextStep}`;
    }
    return composableMessage(reason ?? fallback);
  }

  return composableMessage(
    error instanceof Error ? (safeChineseMessage(error.message) ?? fallback) : fallback,
  );
}
